import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { courtsData } from '@/mocks/homeData';
import StoreChoiceModal from '@/components/feature/StoreChoiceModal';

export default function CourtsSection() {
  const { t } = useTranslation();
  const [storeModalOpen, setStoreModalOpen] = useState(false);

  return (
    <section id="courts" className="relative py-20 md:py-28 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-14">
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase text-accent-600 font-medium mb-3">
              {t('courts.total')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {t('courts.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              {t('courts.subtitle')}
            </p>
          </div>

          {/* Courts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
            {courtsData.map((court) => (
              <div
                key={court.id}
                className="group relative bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-accent-300/40 p-6"
              >
                {/* Court Image */}
                <div className="w-full h-48 rounded-xl overflow-hidden mb-5">
                  <img
                    src={court.image}
                    alt={court.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Court Info */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                    court.type === 'central'
                      ? 'bg-accent-100 text-accent-600'
                      : court.type === 'training'
                      ? 'bg-secondary-100 text-secondary-600'
                      : 'bg-primary-100 text-primary-600'
                  }`}>
                    <i className={`${court.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">
                      {court.name}
                    </h3>
                    <span className={`text-xs font-medium uppercase tracking-wide ${
                      court.type === 'central'
                        ? 'text-accent-600'
                        : court.type === 'training'
                        ? 'text-secondary-600'
                        : 'text-primary-600'
                    }`}>
                      {court.type === 'central' ? t('courts.central') : court.type === 'training' ? t('courts.training') : t('courts.standard')}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                  {court.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5">
                  {court.features.map((feat, idx) => (
                    <span key={idx} className="px-2.5 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200 whitespace-nowrap">
                      {feat}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Padel Coming Soon Banner */}
          <div className="relative rounded-2xl overflow-hidden border-2 border-dashed border-accent-300/50 bg-accent-50/30 p-8 md:p-10 text-center">
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold uppercase tracking-wide">
              À venir
            </div>
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
              <i className="ri-base-station-line text-2xl"></i>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('courts.padel')}
            </h3>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6">
              {t('courts.padelDesc')}
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setStoreModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-smartphone-line"></i>
              {t('courts.cta')}
            </button>
            <button
              onClick={() => setStoreModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold rounded-full bg-background-50 text-primary-600 border-2 border-primary-500 hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-smartphone-line"></i>
              {t('app.download')}
            </button>
          </div>
        </div>
      </div>

      <StoreChoiceModal open={storeModalOpen} onClose={() => setStoreModalOpen(false)} />
    </section>
  );
}